import React, { useRef, useEffect, useState, useCallback } from 'react'
import { evaluateGameStats, subscribeAchievementUnlocked } from './achievements'
import { playOrbSound, playHitSound, playTrophySound } from './audio'

export function ZenRunner({ polar, demo, hrv, onExit }) {
  const canvasRef = useRef(null)

  // Estado del juego
  const [gameState, setGameState] = useState('menu') // 'menu' | 'playing' | 'gameover'
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(() => {
    try { return parseInt(localStorage.getItem('manabi_zen_highscore') || '0', 10) } catch { return 0 }
  })
  const [lives, setLives] = useState(3)
  const [unlockedToast, setUnlockedToast] = useState(null)
  const [lastGameStats, setLastGameStats] = useState(null)

  // Referencias mutables para el bucle del juego a 60 FPS
  const stateRef = useRef({
    ship: { y: 200, vy: 0, r: 16, trail: [] },
    keys: { thrust: false },
    orbs: [],
    hazards: [],
    particles: [],
    speed: 3.5,
    distance: 0,
    score: 0,
    lives: 3,
    calmSeconds: 0,
    calmStreakTimer: 0,
    orbsCollected: 0,
    hitsTaken: 0,
    startTime: 0,
    maxRmssd: 0,
    currentHr: 72,
    isCalm: true,
  })

  // Pulso real o simulado
  const liveHr = (polar && polar.status === 'connected' && polar.hr)
    ? polar.hr
    : (demo && demo.active && demo.hr)
      ? demo.hr
      : 72

  const liveRmssd = hrv?.rmssd || 48
  const isBleConnected = (polar && polar.status === 'connected') || (demo && demo.active)

  // Actualizar referencias biomédicas en tiempo real
  useEffect(() => {
    stateRef.current.currentHr = liveHr
    stateRef.current.isCalm = liveHr <= 75
    if (liveRmssd > stateRef.current.maxRmssd) {
      stateRef.current.maxRmssd = liveRmssd
    }
  }, [liveHr, liveRmssd])

  // Suscripción a notificaciones de logros en vivo
  useEffect(() => {
    const unsub = subscribeAchievementUnlocked(ach => {
      playTrophySound()
      setUnlockedToast(ach)
      setTimeout(() => setUnlockedToast(null), 3600)
    })
    return unsub
  }, [])

  // Iniciar partida
  const startGame = useCallback(() => {
    stateRef.current = {
      ship: { y: 220, vy: 0, r: 15, trail: [] },
      keys: { thrust: false },
      orbs: [],
      hazards: [],
      particles: [],
      speed: 3.6,
      distance: 0,
      score: 0,
      lives: 3,
      calmSeconds: 0,
      calmStreakTimer: 0,
      orbsCollected: 0,
      hitsTaken: 0,
      startTime: Date.now(),
      maxRmssd: liveRmssd,
      currentHr: liveHr,
      isCalm: liveHr <= 75,
    }
    setScore(0)
    setLives(3)
    setGameState('playing')
  }, [liveHr, liveRmssd])

  // Control de teclado y puntero
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        stateRef.current.keys.thrust = true
      }
    }
    const onKeyUp = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        stateRef.current.keys.thrust = false
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  // Bucle de física y renderizado en Canvas a 60 FPS
  useEffect(() => {
    if (gameState !== 'playing') return

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let animId = null
    let lastTime = performance.now()
    let spawnTimer = 0

    const loop = (now) => {
      const dt = Math.min(50, now - lastTime) / 1000
      lastTime = now

      const s = stateRef.current
      const width = canvas.width
      const height = canvas.height

      // 1. Biofeedback: factor de calma basado en frecuencia cardíaca
      const isCalm = s.currentHr <= 75
      const calmMultiplier = isCalm ? 3 : (s.currentHr <= 85 ? 2 : 1)
      const tunnelPad = isCalm ? 40 : 70 // túnel más amplio y seguro si estás en calma

      if (isCalm) {
        s.calmStreakTimer += dt
        if (s.calmStreakTimer >= 1) {
          s.calmSeconds += 1
          s.calmStreakTimer = 0
        }
      } else {
        s.calmStreakTimer = 0
      }

      // 2. Física de la nave
      const gravity = isCalm ? 420 : 540
      const thrustPower = isCalm ? -520 : -620

      if (s.keys.thrust) {
        s.ship.vy += thrustPower * dt
      } else {
        s.ship.vy += gravity * dt
      }

      s.ship.vy = Math.max(-380, Math.min(380, s.ship.vy))
      s.ship.y += s.ship.vy * dt

      // Techo y suelo
      const minY = tunnelPad + s.ship.r
      const maxY = height - tunnelPad - s.ship.r

      if (s.ship.y < minY) {
        s.ship.y = minY
        s.ship.vy = 0
      }
      if (s.ship.y > maxY) {
        s.ship.y = maxY
        s.ship.vy = 0
      }

      // Estela de partículas de la nave
      s.ship.trail.push({ x: 90, y: s.ship.y, r: s.ship.r * 0.7, alpha: 0.8, color: isCalm ? '#64d2ff' : '#ff9f0a' })
      if (s.ship.trail.length > 12) s.ship.trail.shift()
      s.ship.trail.forEach(p => { p.x -= s.speed * 1.5; p.alpha -= 0.05 })

      // 3. Generación de Orbes y Obstáculos
      spawnTimer += dt
      if (spawnTimer > (isCalm ? 0.85 : 1.25)) {
        spawnTimer = 0
        const randY = minY + Math.random() * (maxY - minY)
        if (Math.random() > 0.35) {
          // Generar orbe de energía
          s.orbs.push({
            x: width + 20,
            y: randY,
            r: 12,
            type: Math.random() > 0.8 ? 'bonus' : 'normal',
          })
        } else {
          // Generar obstáculo de turbulencia
          s.hazards.push({
            x: width + 30,
            y: randY,
            w: 24,
            h: 36,
          })
        }
      }

      // Mover orbes y chequear colisiones
      for (let i = s.orbs.length - 1; i >= 0; i--) {
        const o = s.orbs[i]
        o.x -= s.speed * 60 * dt

        // Colisión con la nave (círculo vs círculo)
        const dx = o.x - 90
        const dy = o.y - s.ship.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < o.r + s.ship.r) {
          playOrbSound()
          const pts = (o.type === 'bonus' ? 30 : 10) * calmMultiplier
          s.score += pts
          s.orbsCollected += 1

          // Partículas de recolección
          for (let p = 0; p < 8; p++) {
            s.particles.push({
              x: o.x, y: o.y,
              vx: (Math.random() - 0.5) * 160,
              vy: (Math.random() - 0.5) * 160,
              alpha: 1,
              color: o.type === 'bonus' ? '#ffd700' : '#30d158',
              life: 0.4
            })
          }
          s.orbs.splice(i, 1)
          continue
        }

        if (o.x < -30) s.orbs.splice(i, 1)
      }

      // Mover obstáculos y chequear colisiones
      for (let i = s.hazards.length - 1; i >= 0; i--) {
        const h = s.hazards[i]
        h.x -= s.speed * 60 * dt

        // Colisión simple bounding box
        if (h.x < 90 + s.ship.r && h.x + h.w > 90 - s.ship.r &&
            h.y < s.ship.y + s.ship.r && h.y + h.h > s.ship.y - s.ship.r) {
          playHitSound()
          s.lives -= 1
          s.hitsTaken += 1
          setLives(s.lives)

          // Partículas de choque
          for (let p = 0; p < 12; p++) {
            s.particles.push({
              x: h.x, y: h.y,
              vx: (Math.random() - 0.5) * 220,
              vy: (Math.random() - 0.5) * 220,
              alpha: 1,
              color: '#ff3b30',
              life: 0.5
            })
          }
          s.hazards.splice(i, 1)

          if (s.lives <= 0) {
            // Fin de la partida
            const durationSec = Math.round((Date.now() - s.startTime) / 1000)
            const stats = {
              score: s.score,
              durationSec,
              calmSec: s.calmSeconds,
              orbs: s.orbsCollected,
              hits: s.hitsTaken,
              isConnected: isBleConnected,
              maxRmssd: s.maxRmssd,
            }
            setLastGameStats(stats)
            evaluateGameStats(stats)

            if (s.score > highScore) {
              setHighScore(s.score)
              try { localStorage.setItem('manabi_zen_highscore', String(s.score)) } catch (_) {}
            }
            setGameState('gameover')
            return
          }
          continue
        }

        if (h.x < -50) s.hazards.splice(i, 1)
      }

      // Actualizar partículas
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i]
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.life -= dt
        p.alpha = Math.max(0, p.life / 0.5)
        if (p.life <= 0) s.particles.splice(i, 1)
      }

      // Puntos pasivos por distancia
      s.distance += s.speed * 0.1
      s.score += Math.round(dt * 5 * calmMultiplier)
      setScore(s.score)

      // ── RENDERIZADO VISUAL ──
      ctx.clearRect(0, 0, width, height)

      // Fondo túnel cibernético
      const bgGrad = ctx.createLinearGradient(0, 0, 0, height)
      bgGrad.addColorStop(0, '#060810')
      bgGrad.addColorStop(1, '#0c101c')
      ctx.fillStyle = bgGrad
      ctx.fillRect(0, 0, width, height)

      // Techo y Suelo del Túnel (se adaptan al pulso)
      ctx.fillStyle = isCalm ? 'rgba(0, 122, 255, 0.12)' : 'rgba(255, 59, 48, 0.16)'
      ctx.fillRect(0, 0, width, tunnelPad)
      ctx.fillRect(0, height - tunnelPad, width, tunnelPad)

      ctx.strokeStyle = isCalm ? '#007aff60' : '#ff3b3060'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(0, tunnelPad)
      ctx.lineTo(width, tunnelPad)
      ctx.moveTo(0, height - tunnelPad)
      ctx.lineTo(width, height - tunnelPad)
      ctx.stroke()

      // Estela de la nave
      s.ship.trail.forEach(t => {
        ctx.beginPath()
        ctx.arc(t.x, t.y, t.r, 0, Math.PI * 2)
        ctx.fillStyle = t.color
        ctx.globalAlpha = Math.max(0, t.alpha)
        ctx.fill()
      })
      ctx.globalAlpha = 1

      // Dibujar la Nave Zen
      const shipColor = isCalm ? '#64d2ff' : '#ffd60a'
      ctx.shadowColor = shipColor
      ctx.shadowBlur = isCalm ? 18 : 8

      ctx.beginPath()
      ctx.arc(90, s.ship.y, s.ship.r, 0, Math.PI * 2)
      ctx.fillStyle = shipColor
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()

      // Núcleo palpitante de la nave
      ctx.beginPath()
      ctx.arc(90, s.ship.y, s.ship.r * 0.45, 0, Math.PI * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
      ctx.shadowBlur = 0

      // Dibujar Orbes de Calma
      s.orbs.forEach(o => {
        ctx.shadowColor = o.type === 'bonus' ? '#ffd700' : '#30d158'
        ctx.shadowBlur = 12
        ctx.beginPath()
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2)
        ctx.fillStyle = o.type === 'bonus' ? '#ffd700' : '#30d158'
        ctx.fill()
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 1.5
        ctx.stroke()
        ctx.shadowBlur = 0
      })

      // Dibujar Obstáculos de Turbulencia
      s.hazards.forEach(h => {
        ctx.shadowColor = '#ff3b30'
        ctx.shadowBlur = 10
        ctx.fillStyle = '#ff453a'
        ctx.beginPath()
        ctx.roundRect ? ctx.roundRect(h.x, h.y, h.w, h.h, 6) : ctx.rect(h.x, h.y, h.w, h.h)
        ctx.fill()
        ctx.shadowBlur = 0
      })

      // Dibujar Partículas
      s.particles.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2)
        ctx.fillStyle = p.color
        ctx.globalAlpha = p.alpha
        ctx.fill()
      })
      ctx.globalAlpha = 1

      animId = requestAnimationFrame(loop)
    }

    animId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(animId)
  }, [gameState, highScore, isBleConnected])

  return (
    <div style={{
      position: 'relative', width: '100%', maxWidth: 840, margin: '0 auto',
      background: '#07090e', borderRadius: 18, border: '1px solid rgba(255,255,255,0.12)',
      overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none'
    }}>
      {/* ── 1. Barra Superior HUD en Vivo ── */}
      <div style={{
        padding: '10px 16px', background: 'rgba(255,255,255,0.04)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 10
      }}>
        {/* Pulsaciones en vivo con feedback de color */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 9, height: 9, borderRadius: '50%',
            background: liveHr <= 75 ? '#30d158' : '#ff9f0a',
            boxShadow: `0 0 8px ${liveHr <= 75 ? '#30d158' : '#ff9f0a'}`,
          }} />
          <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {liveHr} <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>BPM</span>
          </span>
          <span style={{
            fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 6,
            background: liveHr <= 75 ? 'rgba(48,209,88,0.2)' : 'rgba(255,159,10,0.2)',
            color: liveHr <= 75 ? '#30d158' : '#ff9f0a'
          }}>
            {liveHr <= 75 ? 'CALMA x3' : liveHr <= 85 ? 'NORMAL x2' : 'ALTO x1'}
          </span>
        </div>

        {/* Puntos y Record */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>
            RÉCORD: <strong style={{ color: '#ffd700' }}>{highScore}</strong>
          </span>
          <span style={{ fontSize: 20, fontWeight: 900, color: '#64d2ff', fontVariantNumeric: 'tabular-nums' }}>
            {score} <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>PTS</span>
          </span>
        </div>

        {/* Escudos de Energía / Vidas */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {[1, 2, 3].map(i => (
            <div
              key={i}
              style={{
                width: 10, height: 10, borderRadius: 2,
                background: i <= lives ? '#30d158' : 'rgba(255,255,255,0.15)',
                boxShadow: i <= lives ? '0 0 6px #30d158' : 'none',
                transition: 'all 0.2s ease'
              }}
            />
          ))}
          <button
            onClick={onExit}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6,
              color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 700,
              padding: '3px 8px', cursor: 'pointer', fontFamily: 'inherit', marginLeft: 8
            }}
          >
            ✕ Salir
          </button>
        </div>
      </div>

      {/* ── 2. Canvas del Juego ── */}
      <div
        onMouseDown={() => { stateRef.current.keys.thrust = true }}
        onMouseUp={() => { stateRef.current.keys.thrust = false }}
        onTouchStart={() => { stateRef.current.keys.thrust = true }}
        onTouchEnd={() => { stateRef.current.keys.thrust = false }}
        style={{ position: 'relative', width: '100%', height: 420, cursor: 'pointer' }}
      >
        <canvas
          ref={canvasRef}
          width={840}
          height={420}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />

        {/* Notificación Toast de Logro Desbloqueado durante la partida */}
        {unlockedToast && (
          <div style={{
            position: 'absolute', top: 16, right: 16,
            background: 'rgba(15, 20, 32, 0.92)', border: '1.5px solid #ffd700',
            borderRadius: 12, padding: '8px 14px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeIn 0.25s ease', backdropFilter: 'blur(12px)', zIndex: 30
          }}>
            <span style={{ fontSize: 16 }}>★</span>
            <div>
              <span style={{ fontSize: 9.5, fontWeight: 800, color: '#ffd700', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block' }}>
                ¡Trofeo Desbloqueado!
              </span>
              <span style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
                {unlockedToast.title}
              </span>
            </div>
          </div>
        )}

        {/* ── Pantalla de Inicio / Menú ── */}
        {gameState === 'menu' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(15,20,32,0.92) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <div style={{
              width: 54, height: 54, borderRadius: '50%', background: 'rgba(100,210,255,0.15)',
              border: '2px solid #64d2ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 24px rgba(100,210,255,0.3)', marginBottom: 14
            }}>
              <span style={{ fontSize: 24, color: '#64d2ff' }}>▲</span>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 900, color: '#fff', margin: '0 0 6px', letterSpacing: -0.5 }}>
              Nave Zen: Vuelo en Calma
            </h2>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)', maxWidth: 440, margin: '0 0 18px', lineHeight: 1.45 }}>
              Mantené pulsada la pantalla o la tecla <strong>Espacio</strong> para propulsar la nave.
              <br />
              <strong style={{ color: '#30d158' }}>Mecánica biológica:</strong> Si mantenés tus pulsaciones por debajo de 75 BPM, el túnel se ensancha y tus puntos se multiplican x3.
            </p>
            <button
              onClick={startGame}
              style={{
                background: '#007aff', color: '#fff', border: 'none',
                borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 18px rgba(0,122,255,0.4)',
                transition: 'transform 0.15s ease'
              }}
            >
              ¡DESPEGAR AHORA!
            </button>
          </div>
        )}

        {/* ── Pantalla de Game Over ── */}
        {gameState === 'gameover' && lastGameStats && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle, rgba(20,12,14,0.94) 0%, rgba(6,8,14,0.98) 100%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24, textAlign: 'center', zIndex: 20
          }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#ff3b30', textTransform: 'uppercase', letterSpacing: 1 }}>
              Turbulencia Crítica
            </span>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '4px 0 10px' }}>
              Fin del Vuelo
            </h2>

            {/* Fichas de Estadísticas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, width: '100%', maxWidth: 380, marginBottom: 18 }}>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Puntos</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#64d2ff' }}>{lastGameStats.score}</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Tiempo Zen</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#30d158' }}>{lastGameStats.calmSec}s</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: 8 }}>
                <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}>Orbes</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: '#ffd700' }}>{lastGameStats.orbs}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={startGame}
                style={{
                  background: '#30d158', color: '#000', border: 'none',
                  borderRadius: 10, padding: '10px 22px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Volar de Nuevo
              </button>
              <button
                onClick={() => setGameState('menu')}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Menú
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Instrucción al pie ── */}
      <div style={{
        padding: '8px 16px', background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)'
      }}>
        <span>Controles: Clic, toque en pantalla o barra de espacio</span>
        <span>Biofeedback: Polar H10 a 130 Hz</span>
      </div>
    </div>
  )
}
